import React, {useContext, useEffect, useRef, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import deepEquals from "fast-deep-equal";
import {BankingAccountForm} from "./BankingAccountForm";
import {BankingAccountFormData, BankingAccountsSummary} from "../../models/BankingAccounts";
import AssetsViewContext from "../../common/AssetsViewContext";
import {selectProfile} from "../../../ClientManagement/ClientProfile/activeProfileSlice";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {TaxDetailsType} from "../../models/TaxDetails";
import {RouteWithAssetId} from "../../../routes/types";
import {MemberGroup} from "../../../ClientManagement/models/InvestorGroupType";
import {LegalEntityFormData, OwnershipDetailsFormData} from "../../models/Ownership";
import {assetsApiClient} from "../../AssetsApiClient";
import {clientManagementApiClient} from "../../../ClientManagement/ClientManagementApiClient";
import {
    extractOwnershipDetailsFormData,
    mapToOwnershipDetailsFormData,
    mapToOwnershipWriteModel
} from "../../Ownership/mappers";
import {setActiveFormAsset} from "../../clientAssetsSlice";
import {hasSomeInEstateOwnership} from "../../Ownership/helpers";
import {isOwnershipPercentageNotEqual100} from "../../Ownership/validation";
import LoadingIndicator from "../../../pages/LoadingIndicator";
import {HistoryBlockModal} from "../../../components/HistoryBlockModal/HistoryBlockModal";
import DataEntryHeader from "../../../components/DataEntry/DataEntryHeader";
import {RequiredFieldsBanner} from "../../../components";
import {AccountSummary} from "../../AccountsCommon/AccountSummary";
import DiscardAssetModal from "../../DiscardAssetModal";
import {HoldingSummary} from "../../models/Holding";
import {AssetClassifications} from "../../models/AssetClassifications";
import useProfileEditableState from "../../../hooks/useProfileEditableState";
import useAnalytics from "../../../utils/useAnalytics";
import {useAnalyticsTrackers} from "../../../utils/useAnalyticsTrackers";

export default function EditBankingAccount() {
    const history = useHistory();
    const viewType = useContext(AssetsViewContext);
    const profile = useAppSelector(selectProfile);
    const {isProfileWithProposalsOrArchived} = useProfileEditableState();
    const [isSaveButtonDisabled, updateSaveButtonDisabled] = useState(isProfileWithProposalsOrArchived);
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const [bankingAccount, setBankingAccount] = useState<BankingAccountsSummary>();
    const [initialBankingAccount, setInitialBankingAccount] = useState<BankingAccountsSummary>();
    const [taxDetails_, setTaxDetails_] = useState<TaxDetailsType>();
    const [initialTaxDetails_, setInitialTaxDetails_] = useState<TaxDetailsType>();
    const [bankingAccountHoldings_, setBankingAccountHoldings_] = useState<HoldingSummary>();
    const [unrealizedCapitalGainsTax_, updateUnrealizedCapitalGainsTax_] = useState<number | null>(null);
    const [deferredTaxLiability_, updateDeferredTaxLiability_] = useState<number | null>(null);

    const [isRequiredFieldsBannerShown_, setRequiredFieldsBannerShown_] = useState(false);
    const [memberGroup_, setMemberGroup_] = useState<MemberGroup>();
    const [legalEntities_, updateLegalEntities_] = useState<LegalEntityFormData[]>();
    const [ownershipDetailsFormData_, updateOwnershipDetailsFormData_] = useState<OwnershipDetailsFormData>();
    const [initialOwnershipDetailsFormData_, setInitialOwnershipDetailsFormData_] = useState<OwnershipDetailsFormData>();
    const [isOwnershipPercentageErrorBannerShown_, setOwnershipPercentageErrorBannerShown_] = useState(false);
    const [showNavigationModal_, setShowNavigationModal_] = useState(true);
    const [classifications_, setClassifications_] = useState<AssetClassifications>();
    const {assetId: bankingAccountId} = useParams<RouteWithAssetId>();
    const [totalInvestablePresentValue_, updateTotalInvestablePresentValue_] = useState<number | null>(null);
    const {page} = useAnalyticsTrackers("enableAssetsGoogleAnalytics");
    const dispatch = useAppDispatch();
    const mounted = useRef(false);


    useAnalytics(
        "Navigate_Edit_Banking_Account_Form",
        "Navigate_Edit_Banking_Account_Form_TimeSpent",
        "enableAssetsGoogleAnalytics"
    );

    useEffect(() => {
        Promise.all([
            assetsApiClient.getBankingAccountHoldings(profile.id, bankingAccountId ),
            assetsApiClient.getBankingAccountById(profile.id,bankingAccountId ),
            clientManagementApiClient.getMemberGroup(profile.id),
            assetsApiClient.getLegalEntities(profile.id)
        ]).then(([   holdingsResponse,
                     bankingAccountResponse,
                     memberGroupResponse,
                     legalEntitiesResponse]) => {
            setTaxDetails_({
                isEntityCapitalGains: bankingAccountResponse.isEntityCapitalGains!,
                isLiabilityPaidByPortfolio: bankingAccountResponse.isLiabilityPaidByPortfolio!,
            });

            setInitialTaxDetails_({
                isEntityCapitalGains: bankingAccountResponse.isEntityCapitalGains,
                isLiabilityPaidByPortfolio: bankingAccountResponse.isLiabilityPaidByPortfolio,
            });

            setBankingAccountHoldings_(holdingsResponse);
            updateUnrealizedCapitalGainsTax_(holdingsResponse.unrealizedCapitalGainsTax);
            updateDeferredTaxLiability_(holdingsResponse.deferredTaxLiability);
            updateTotalInvestablePresentValue_(holdingsResponse.totalInvestablePresentValue || null);
            setBankingAccount(bankingAccountResponse);
            setInitialBankingAccount(bankingAccountResponse);
            updateOwnershipDetailsFormData_(extractOwnershipDetailsFormData(
                mapToFormData(bankingAccountResponse)));
            setInitialOwnershipDetailsFormData_(extractOwnershipDetailsFormData(
                mapToFormData(bankingAccountResponse)));
            setMemberGroup_(memberGroupResponse);
            updateLegalEntities_(legalEntitiesResponse);

            dispatch(setActiveFormAsset({
                assetType: 'bankingAccount',
                id: bankingAccountResponse.id || undefined,
                inEstateValue: holdingsResponse.marketValue ? holdingsResponse.marketValue?.inEstateValue || 0 : 0,
                description: bankingAccountResponse.accountName,
                hasInEstateOwnership: hasSomeInEstateOwnership(bankingAccountResponse.memberOwnerships),
            }));
        }).catch(error => console.error('Could not fetch holding data', error.message));
    }, [profile.id, bankingAccountId]);

    useEffect(() => {
        mounted.current = true;
        if (bankingAccount?.taxStatus === "Deferred") {
            const updatedTaxDetails: TaxDetailsType = {
                isEntityCapitalGains: null,
                isLiabilityPaidByPortfolio: bankingAccount.isLiabilityPaidByPortfolio
            };
            setTaxDetails_(updatedTaxDetails);
            handleDeferredTaxLiabilityChange()
                .catch(reason => console.log(reason));
            updateUnrealizedCapitalGainsTax_(null);
        } else if (bankingAccount?.taxStatus === "Taxable" && taxDetails_?.isEntityCapitalGains !== null) {
            handleUnrealizedCapitalGainsTaxChange(taxDetails_?.isEntityCapitalGains)
                .catch(reason => console.log(reason));
            updateDeferredTaxLiability_(null);
        } else {
            updateUnrealizedCapitalGainsTax_(null);
            updateDeferredTaxLiability_(null);
        }

        if((bankingAccount?.taxStatus === "Deferred" || bankingAccount?.taxStatus === "Taxable") && taxDetails_?.isLiabilityPaidByPortfolio) {
            getInvestablePresentValue(taxDetails_?.isLiabilityPaidByPortfolio).then();
        }
        return () => {
            mounted.current = false;
        }
    }, [bankingAccount?.taxStatus]);


    useEffect(() => {
        setRequiredFieldsBannerShown_(isRequiredFieldsBannerShown_ && isAnyRequiredFieldEmpty());
    }, [taxDetails_, bankingAccount?.accountName, ownershipDetailsFormData_?.legalEntityOwnerships]);

    useEffect(() => {
        setOwnershipPercentageErrorBannerShown_(isOwnershipPercentageErrorBannerShown_ &&
            isOwnershipPercentageNotEqual100(ownershipDetailsFormData_!));
    }, [ownershipDetailsFormData_?.legalEntityOwnerships, ownershipDetailsFormData_?.memberOwnerships]);

    useEffect(() => {
        assetsApiClient.getAssetClassifications().then(assetClassificationResponse => setClassifications_(assetClassificationResponse));
    }, []);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            dispatch(setActiveFormAsset(null));
        }
    }, []);

    const handleDeferredTaxLiabilityChange = async () => {
        if (initialBankingAccount?.id) {
            let deferredTaxLiabilityResponse = await assetsApiClient.getDeferredTaxLiabilityForBankingAccount(profile.id, initialBankingAccount?.id);
            if (mounted.current) updateDeferredTaxLiability_(deferredTaxLiabilityResponse);
        }
    }

    const isAnyRequiredFieldEmpty = () => {
        const isNameBlank = !bankingAccount?.accountName.trim();
        const isLiabilityQuestionVisibleForTaxable = bankingAccount!.taxStatus === "Taxable" && !!taxDetails_!.isEntityCapitalGains;
        const isLiabilityQuestionVisibleForDeferred = bankingAccount!.taxStatus === "Deferred";
        const isLiabilityQuestionVisible = isLiabilityQuestionVisibleForTaxable || isLiabilityQuestionVisibleForDeferred;
        const isLiabilityQuestionBlank = taxDetails_!.isLiabilityPaidByPortfolio === null;
        const isOwnershipDataMissing = ownershipDetailsFormData_?.legalEntityOwnerships.some((ownership) => {
            return !ownership.name.trim() || !ownership.type;
        });
        return isNameBlank || isOwnershipDataMissing || (isLiabilityQuestionBlank && isLiabilityQuestionVisible);
    }
    const onOwnershipFormChange = (ownershipFormData: OwnershipDetailsFormData) => {
        updateOwnershipDetailsFormData_(ownershipFormData);
    }
    const handleSave = async () => {
        const {isValid} = validateForm();
        if (isValid) {
            updateSaveButtonDisabled(true);
            setShowNavigationModal_(false);
            const response = await assetsApiClient.updateBankAccount(profile.id, bankingAccountId , {

                ...mapToBankingAccountModel(bankingAccount!),

                isEntityCapitalGains: taxDetails_!.isEntityCapitalGains,
                isLiabilityPaidByPortfolio: taxDetails_!.isLiabilityPaidByPortfolio,
                ...mapToOwnershipWriteModel(ownershipDetailsFormData_!),
                doesPermitBeneficiary: bankingAccount?.doesPermitBeneficiary ?? false,
            });
            if (response.status === 200) {
                page(`Edit_Banking_Account_Save_Click`);
                navigateToAssetsView();
            }
            return true;
        }
        return false;
    }
    const navigateToAssetsView = () => {
        history.push(`/Profile/${profile.id}/ClientProfile/${viewType}`);
    }

    const isFormChanged = () => {
        const updated = {
            ...bankingAccount,
            ...taxDetails_,
            ...ownershipDetailsFormData_
        };
        const initial = {
            ...initialBankingAccount,
            ...initialTaxDetails_,
            ...initialOwnershipDetailsFormData_
        }
        return !deepEquals(initial, updated);
    }
    const handleCancel = () => {
        if (isFormChanged()) {
            setShowDiscardModal(true);
            setShowNavigationModal_(false);
            return;
        }
        navigateToAssetsView();
    }

    const handleUnrealizedCapitalGainsTaxChange = async (isEntityCapitalGains: boolean=false) => {
        updateUnrealizedCapitalGainsTax_(await assetsApiClient.getUnrealizedCapitalGainsTaxBankingAccount(
            profile.id,
            bankingAccountId ,
            isEntityCapitalGains
        ));
    }
    const handleClickViewHoldings = async () => {
        const {isValid} = validateForm();
        if (isValid) {
            setShowNavigationModal_(false);
            const response = await assetsApiClient.updateBankAccount(profile.id, bankingAccountId , {

                ...mapToBankingAccountModel(bankingAccount!),

                isEntityCapitalGains: taxDetails_!.isEntityCapitalGains,
                isLiabilityPaidByPortfolio: taxDetails_!.isLiabilityPaidByPortfolio,
                ...mapToOwnershipWriteModel(ownershipDetailsFormData_!)
            });
            if (response.status === 200) {
                history.push(`/Profile/${profile.id}/ClientProfile/${viewType}/BankingAccount/${bankingAccountId}/Holdings`);
            }
        }
    };
    const validateForm = () => {
        const isRequiredFieldEmpty = isAnyRequiredFieldEmpty();
        if (isRequiredFieldEmpty) {
            setRequiredFieldsBannerShown_(true);
        }
        const isOwnershipPercentageInvalid = isOwnershipPercentageNotEqual100(ownershipDetailsFormData_!);
        if (isOwnershipPercentageInvalid) {
            setOwnershipPercentageErrorBannerShown_(true);
        }
        return {
            isValid: !isRequiredFieldEmpty && !isOwnershipPercentageInvalid
        };
    };
    const getInvestablePresentValue = async (liabilityPaidByPortfolio: boolean) => {
        if (liabilityPaidByPortfolio && bankingAccount?.id) {
            let totalInvestablePresentValueResponse = await assetsApiClient.getInvestablePresentValueForBankingAccount(
                profile.id, bankingAccount.id, bankingAccount.taxStatus);
            if (mounted.current) updateTotalInvestablePresentValue_(totalInvestablePresentValueResponse);
        } else {
            updateTotalInvestablePresentValue_(null);
        }
    }


    const handleDelete = async () => {
        const response = await assetsApiClient.deleteBankingAccount(profile.id, bankingAccountId );
        if (response.status === 200) {
            navigateToAssetsView();
        }
    };

    if (!(
        bankingAccount
        && bankingAccountHoldings_
        && ownershipDetailsFormData_
        && taxDetails_
        && classifications_
        && memberGroup_
        && legalEntities_)) {
        return <LoadingIndicator/>;
    }
    return (
        <div className="held-away-account asset-form">
            <div className="layout-data-entry-form">
                <HistoryBlockModal
                    when={isFormChanged() && showNavigationModal_}
                    itemType={'page'}
                    onSave={handleSave}
                />
                <DataEntryHeader
                    className='dataEntryHeader'
                    title={`Edit ${bankingAccount?.accountName}`}
                    onPrimaryButtonClick={handleSave}
                    onSecondaryButtonClick={handleCancel}
                    disablePrimaryButton={isSaveButtonDisabled}
                    primaryButtonText="Save"
                    secondaryButtonText="Cancel"
                />
                <RequiredFieldsBanner showAlert={isRequiredFieldsBannerShown_} itemType="banking account"/>
                <div className="held-away-account__form layout-data-entry-form">
                    <BankingAccountForm
                        ownershipDetailsFormData={ownershipDetailsFormData_}
                        isRequiredFieldsBannerShown={isRequiredFieldsBannerShown_}
                        isOwnershipPercentageErrorBannerShown={isOwnershipPercentageErrorBannerShown_}
                        handleBankingAccountChange={setBankingAccount}
                        taxDetails={taxDetails_}
                        handleTaxDetailsChange={setTaxDetails_}
                        unrealizedCapitalGainsTax={unrealizedCapitalGainsTax_}
                        deferredTaxLiability={deferredTaxLiability_}
                        totalInvestablePresentValue={totalInvestablePresentValue_}
                        getInvestablePresentValue={getInvestablePresentValue}
                        handleUnrealizedCapitalGainsTaxChange={handleUnrealizedCapitalGainsTaxChange}
                        memberGroup={memberGroup_}
                        legalEntities={legalEntities_}
                        handleLegalEntitiesChange={updateLegalEntities_}
                        onOwnershipFormChange={onOwnershipFormChange}
                        bankingAccount={bankingAccount!}
                        bankingAccountHoldings={bankingAccountHoldings_}
                        handleDelete={handleDelete}
                    />
                    <AccountSummary
                        assetType={'bankingAccount'}
                        holdings={bankingAccountHoldings_}
                        unrealizedCapitalGainsTax={unrealizedCapitalGainsTax_}
                        deferredTaxLiability={deferredTaxLiability_}
                        onClick={handleClickViewHoldings}
                        classifications={classifications_}
                    />
                </div>
            </div>
            <DiscardAssetModal
                isOpen={showDiscardModal}
                title={`Discard changes to this Asset?`}
                content={`Any data entered for this asset will not be saved.`}
                onClickKeepEditing={() => setShowDiscardModal(false)}
                onClickDiscardChanges={navigateToAssetsView}
            />
        </div>
    );
}

function mapToFormData(bankingAccount: BankingAccountsSummary): BankingAccountFormData {
    return {
        ...bankingAccount,
        ...mapToOwnershipDetailsFormData(bankingAccount),
    };
}

function mapToBankingAccountModel(bankAccount: BankingAccountsSummary) {
    const {ownershipCategory, memberOwnerships, legalEntityOwnerships, ...bankingAccount} = bankAccount;

    return bankingAccount;
}
